import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AdminLayout from '../../components/layout/AdminLayout'
import AdminContentLayout from '../../components/layout/AdminContentLayout'
import BadgeCard from '../../components/individual/BadgeCard'
import BadgeSummaryCard from '../../components/individual/BadgeSummaryCard'
import AvatarDisplay from '../../components/individual/AvatarDisplay'
import { useAuthStore } from '../../store/useAuthStore'
import useRewardStore from '../../store/rewardStore'
import { apiRequest } from '../../utils/api'

const SIDEBAR_CATEGORIES = [
  { key: 'all',      label: 'All Badges', icon: '/assets/icons/medal.png',  dbKey: null },
  { key: 'learning', label: 'Learning',   icon: '/assets/icons/book.png',   dbKey: 'learning' },
  { key: 'streak',   label: 'Streak',     icon: '/assets/icons/streak.png', dbKey: 'streak' },
  { key: 'review',   label: 'Review',     icon: '/assets/icons/target.png', dbKey: 'review' },
  { key: 'accuracy', label: 'Accuracy',   icon: '/assets/icons/star.png',   dbKey: 'accuracy' },
  { key: 'progress', label: 'Special',    icon: '/assets/icons/trophy.png', dbKey: 'progress' },
]

const BadgeCollection = () => {
  const navigate = useNavigate()
  const user     = useAuthStore((state) => state.user)
  const { coins, gems, avatar } = useRewardStore()

  const [collection,   setCollection]   = useState(null)   // API 응답 COLLECTION 객체
  const [earnedBadges, setEarnedBadges] = useState([])     // 획득 배지 + earned_at
  const [activeTab,    setActiveTab]    = useState('all')
  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState('')

  useEffect(() => {
    if (!user?.USER_ID) return
    loadData()
  }, [user?.USER_ID])

  const loadData = async () => {
    setLoading(true)
    setError('')
    try {
      const [colRes, earnedRes] = await Promise.all([
        apiRequest('/badges/collection', 'POST', { USER_ID: user.USER_ID }),
        apiRequest('/badges/earned',     'POST', { USER_ID: user.USER_ID }),
      ])
      if (colRes.success === 'Y')    setCollection(colRes.data)
      if (earnedRes.success === 'Y') setEarnedBadges(earnedRes.data.EARNED_BADGES || [])
    } catch (err) {
      setError('Failed to load badges. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // ── 파생 데이터 ──────────────────────────────────────────────
  const allBadges = collection
    ? Object.values(collection.COLLECTION).flatMap(c => c.BADGES)
    : []

  const earnedMap = earnedBadges.reduce((m, b) => {
    m[b.badge_key] = b.earned_at
    return m
  }, {})

  const getCategoryBadges = (tabKey) => {
    if (!collection) return []
    if (tabKey === 'all') return allBadges
    return collection.COLLECTION[tabKey]?.BADGES || []
  }

  const activeBadges = getCategoryBadges(activeTab)
  const earned  = activeBadges.filter(b => b.is_earned)
  const locked  = activeBadges.filter(b => !b.is_earned)

  // 사이드바 카운트
  const getCounts = (dbKey) => {
    if (!collection) return { earned: 0, total: 0 }
    if (!dbKey) return { earned: collection.TOTAL_EARNED, total: collection.TOTAL_AVAILABLE }
    const cat = collection.COLLECTION[dbKey]
    return { earned: cat?.EARNED || 0, total: cat?.TOTAL || 0 }
  }

  // Recently Earned: 가장 최근 earned_at 기준
  const recentBadge = earnedBadges.length > 0
    ? [...earnedBadges].sort((a, b) => new Date(b.earned_at) - new Date(a.earned_at))[0]
    : null

  // ────────────────────────────────────────────────────────────
  return (
    <AdminLayout>
      <AdminContentLayout>

        {/* 전체 페이지 헤더 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg,#FEF3C7,#FDE68A)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img src="/assets/icons/trophy.png" alt="trophy" style={{ width: 28, height: 28, objectFit: 'contain' }} /></div>
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: '#0D2A4F' }}>Badge Collection</h1>
              <p style={{ margin: 0, fontSize: 13, color: '#7A889B' }}>Collect badges and show off your achievements!</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <BalanceChip src="/assets/icons/coin.png" value={coins} />
            <BalanceChip src="/assets/icons/gem.png" value={gems} />
            <button onClick={() => navigate('/dashboard/individual')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#7A889B', padding: 4 }}>✕</button>
          </div>
        </div>

        {/* 2열 레이아웃 */}
        <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 20, alignItems: 'start' }}>

          {/* 좌: 사이드바 */}
          <div style={panelStyle}>
            {/* 아바타 */}
            <div style={{
              borderRadius: 16,
              overflow: 'hidden',
              marginBottom: 10,
            }}>
              <AvatarDisplay
                avatar={avatar}
                userAvatar={user?.AVATAR}
                size={208}
                showSparkles={false}
                naked
              />
            </div>

            {/* 배지 수 */}
            <p style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: '#4F6BED', textAlign: 'center' }}>
              {getCounts(null).earned} / {getCounts(null).total} Badges
            </p>

            {/* 카테고리 필터 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {SIDEBAR_CATEGORIES.map(cat => {
                const counts   = getCounts(cat.dbKey)
                const isActive = activeTab === cat.key
                return (
                  <button
                    key={cat.key}
                    onClick={() => setActiveTab(cat.key)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '9px 12px',
                      borderRadius: 12,
                      border: 'none',
                      background: isActive ? '#4F6BED' : 'transparent',
                      color: isActive ? '#fff' : '#64748B',
                      cursor: 'pointer',
                      transition: 'background .12s',
                      width: '100%',
                      textAlign: 'left',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <img src={cat.icon} alt={cat.label} style={{ width: 16, height: 16, objectFit: 'contain' }} />
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{cat.label}</span>
                    </div>
                    <span style={{
                      fontSize: 11,
                      fontWeight: 700,
                      background: isActive ? 'rgba(255,255,255,.22)' : '#EDF2F7',
                      color: isActive ? '#fff' : '#94A3B8',
                      borderRadius: 20,
                      padding: '2px 8px',
                      minWidth: 36,
                      textAlign: 'center',
                    }}>
                      {counts.earned} / {counts.total}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* 우: 메인 콘텐츠 */}
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#B0BBC8' }}>
                <p style={{ fontSize: 32 }}>⏳</p>
                <p style={{ margin: 0 }}>Loading badges…</p>
              </div>
            ) : error ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <p style={{ color: '#DC2626', fontSize: 14 }}>{error}</p>
                <button onClick={loadData} style={{ marginTop: 12, padding: '10px 20px', background: '#4F6BED', color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer', fontWeight: 700 }}>Retry</button>
              </div>
            ) : (
              <>
                {/* 요약 카드 */}
                <BadgeSummaryCard
                  totalEarned={collection?.TOTAL_EARNED || 0}
                  totalAvailable={collection?.TOTAL_AVAILABLE || 0}
                  allBadges={allBadges}
                  earnedBadges={earnedBadges}
                  recentBadge={recentBadge}
                  onViewRecent={() => setActiveTab(recentBadge ? recentBadge.category : 'all')}
                />

                {/* Earned Badges 섹션 */}
                {earned.length > 0 && (
                  <div style={{ marginBottom: 28 }}>
                    <p style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 800, color: '#0D2A4F' }}>
                      <img src="/assets/icons/star.png" alt="earned" style={{ width: 14, height: 14, objectFit: 'contain', verticalAlign: 'middle', marginRight: 4 }} />Earned Badges <span style={{ fontSize: 13, color: '#7A889B', fontWeight: 600 }}>({earned.length})</span>
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 12 }}>
                      {earned.map(badge => (
                        <BadgeCard
                          key={badge.badge_key}
                          badge={badge}
                          isEarned={true}
                          earnedDate={earnedMap[badge.badge_key] || null}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Locked Badges 섹션 */}
                {locked.length > 0 && (
                  <div>
                    <p style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 800, color: '#0D2A4F' }}>
                      <img src="/assets/icons/lock.png" alt="locked" style={{ width: 14, height: 14, objectFit: 'contain', verticalAlign: 'middle', marginRight: 4 }} />Locked Badges <span style={{ fontSize: 13, color: '#7A889B', fontWeight: 600 }}>({locked.length})</span>
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 12 }}>
                      {locked.map(badge => (
                        <BadgeCard
                          key={badge.badge_key}
                          badge={badge}
                          isEarned={false}
                          earnedDate={null}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* 배지 없음 */}
                {earned.length === 0 && locked.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '60px 0', color: '#B0BBC8' }}>
                    <img src="/assets/icons/medal.png" alt="badge" style={{ width: 36, height: 36, objectFit: 'contain' }} />
                    <p style={{ margin: 0, fontSize: 14 }}>No badges in this category</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

      </AdminContentLayout>
    </AdminLayout>
  )
}

const panelStyle = {
  background: '#ffffff',
  border: '1px solid #E7ECF3',
  borderRadius: 24,
  padding: 16,
  boxShadow: '0 10px 30px rgba(13,42,79,.06)',
}

const BalanceChip = ({ src, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F8FBFF', border: '1px solid #E7ECF3', borderRadius: 12, padding: '8px 14px' }}>
    <img src={src} alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />
    <span style={{ fontSize: 15, fontWeight: 800, color: '#0D2A4F' }}>{value?.toLocaleString() ?? 0}</span>
  </div>
)

export default BadgeCollection
